import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Box, Button, Center, Checkbox, Group, List, Loader, Modal, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconCheck, IconMinus } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { api, bearer } from '../../api/client';
import { logEvent } from '../../lib/telemetry';
import { catalogueProducts, DEFAULT_CATALOGUE_PRODUCT, type CatalogueProduct } from '../../staff/catalogueProducts';
import {
  toDraft, featureGrid, toWrites, isDraftEmpty, diffCatalogue, slugOf, revertChange,
  type Draft, type ModuleDraft, type OfferDraft, type CatalogueWrite, type ItemStatus,
} from '../../staff/catalogue';
import { summarise, retireWarnings } from '../../staff/changeSummary';
import { publishPackaging, type PublishReport, type WriteOutcome } from '../../staff/publishPackaging';
import { makePut } from '../../api/catalogueWrites';
import { slugFault, validateChanges, type Fault, type Violation } from '../../staff/catalogueValidation';
import { canEditCatalogue } from '../../staff/staffAccess';
import { ModuleDrawer } from './ModuleDrawer';
import { OfferDrawer } from './OfferDrawer';
import { PublishDialog } from './PublishDialog';
import type { components } from '../../api/generated/entitlements';
import type { ReactNode } from 'react';

type Item = components['schemas']['CatalogueItemView'];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; baseline: Item[] }
  | { kind: 'error'; status: number };

type DrawerState = { kind: 'module'; item: ModuleDraft } | { kind: 'offer'; item: OfferDraft } | null;

// Toggle a value in a set-like list: drop it if present, append it otherwise.
const toggleInList = (list: string[], value: string): string[] =>
  (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

// Read-only grant indicator for the feature matrix when the viewer cannot edit.
const grantIcon = (granted: boolean) => (granted
  ? <IconCheck size={16} color="var(--status-success)" aria-label="granted" />
  : <IconMinus size={16} color="var(--text-subtle)" aria-label="not granted" />);

const FAILED = 'cloudhouse.enterprise.catalogue.admin_load_failed';
const OPENED = 'cloudhouse.enterprise.catalogue.admin_opened';
const EDIT_OPENED = 'cloudhouse.enterprise.catalogue.edit_opened';
const PUBLISH_STARTED = 'cloudhouse.enterprise.catalogue.publish_started';
const PUBLISH_COMPLETED = 'cloudhouse.enterprise.catalogue.publish_completed';
const WRITE_REJECTED = 'cloudhouse.enterprise.catalogue.write_rejected';
const WRITE_UNAUDITED = 'cloudhouse.enterprise.catalogue.write_unaudited';
const PUBLISH_BLOCKED = 'cloudhouse.enterprise.catalogue.publish_blocked';
const ITEM_RETIRED = 'cloudhouse.enterprise.catalogue.item_retired';
const ITEM_RESTORED = 'cloudhouse.enterprise.catalogue.item_restored';
const CHANGE_DISCARDED = 'cloudhouse.enterprise.catalogue.change_discarded';

// Publish-gate fault copy. Only these three are reachable at the gate; slug faults are caught
// inline at Add (SLUG_FAULT_COPY), so they map to ''.
const FAULT_COPY: Record<Fault, string> = {
  feature_name_blank: 'A feature name cannot be blank',
  confers_nothing: 'A trial offer must confer at least one tier or module',
  duration_out_of_range: 'Trial duration must be between 1 and 365 days',
  slug_missing: '', slug_malformed: '', slug_too_long: '', already_exists: '',
};

function faultCounts(violations: readonly Violation[]): Record<string, number> {
  return violations.reduce<Record<string, number>>(
    (acc, v) => ({ ...acc, [v.fault]: (acc[v.fault] ?? 0) + 1 }), {});
}

// Retirement is carried on `status` for tiers/modules and on `availability` for offers.
const isRetired = (fields: { status?: string | null; availability?: string | null }, kind: string): boolean =>
  kind === 'trial-offer' ? fields.availability === 'retired' : fields.status === 'retired';

// Emit the lifecycle event on a confirmed publish only when the write crossed the retire boundary
// — intent (a draft click) is not fact until the server accepts it (mirrors the write_* events).
function logRetireTransition(write: CatalogueWrite, baseline: Item[], productId: string): void {
  const kind = kindOfId(write.id);
  const before = isRetired(baseline.find((i) => i.id === write.id) ?? {}, kind);
  const after = isRetired(write.body, kind);
  if (after && !before) logEvent(ITEM_RETIRED, 'Catalogue item retired', { product_id: productId, item_id: write.id, kind });
  else if (!after && before) logEvent(ITEM_RESTORED, 'Catalogue item restored', { product_id: productId, item_id: write.id, kind });
}

// Item ids are "<kind>:<slug>" (catalogue.ts's slugOf convention); a freshly-published create
// has no baseline counterpart yet, so its Item has to be built from the write itself.
function kindOfId(id: string): string {
  if (id.startsWith('tier:')) return 'tier';
  if (id.startsWith('module:')) return 'module';
  return 'trial-offer';
}

function toItem(write: CatalogueWrite, version: number | string): Item {
  return { id: write.id, productId: write.productId, type: kindOfId(write.id), version, ...write.body };
}

// Folds one publish outcome back into the baseline so a retry only re-sends what's still
// pending. `published` catches the item fully up to the draft (body + version). An `unaudited`
// outcome with a fresh version was actually written server-side (retry-safe) so only its version
// moves — the body stays diffed against the draft until a real reload confirms it. Conflicts,
// rejections, and an `unaudited` with no version leave the item untouched: still pending, needs
// a reload (PublishDialog carries that copy per row from the report).
function foldOutcome(baseline: Item[], write: CatalogueWrite, outcome: WriteOutcome): Item[] {
  if (outcome.kind === 'published') {
    const item = toItem(write, outcome.version);
    return baseline.some((i) => i.id === write.id)
      ? baseline.map((i) => (i.id === write.id ? item : i))
      : [...baseline, item];
  }
  if (outcome.kind === 'unaudited' && outcome.version !== undefined) {
    const version = outcome.version;
    return baseline.map((i) => (i.id === write.id ? { ...i, version } : i));
  }
  return baseline;
}

export function StaffCatalogue() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { productId } = useParams();
  const product = catalogueProducts.find((p) => p.id === productId);
  const writable = canEditCatalogue(auth.permissions);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [report, setReport] = useState<PublishReport | null>(null);
  const [confirmDiscardAll, setConfirmDiscardAll] = useState(false);

  useEffect(() => {
    if (!product) return;
    const ctrl = new AbortController();
    void (async () => {
      setState({ kind: 'loading' });
      const fail = (status: number) => {
        logEvent(FAILED, 'Staff catalogue load failed', { product_id: product.id, status }, 'WARN');
        setState({ kind: 'error', status });
      };
      try {
        const token = await auth.getAccessToken();
        if (!token) { fail(0); return; }
        const { data, error, response } = await api.GET('/admin/catalogue/{productId}', {
          params: { path: { productId: product.id } },
          headers: bearer(token),
          signal: ctrl.signal,
        });
        if (error || !data) { fail(response?.status ?? 0); return; }
        const tierCount = data.items.filter((i) => i.type === 'tier').length;
        setState({ kind: 'ready', baseline: data.items });
        setDraft(toDraft(data.items, product.id));
        logEvent(OPENED, 'Staff opened a product catalogue',
          { product_id: product.id, item_count: data.items.length, tier_count: tierCount });
        logEvent(EDIT_OPENED, 'Staff catalogue edit surface opened', { product_id: product.id, writable });
      } catch (e) {
        if ((e as Error).name !== 'AbortError') fail(0);
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on productId + reloadKey; auth/writable intentionally excluded (unstable ref / derived from auth)
  }, [product?.id, reloadKey]);

  if (!product) return <Navigate to={`/staff/catalogue/${DEFAULT_CATALOGUE_PRODUCT}`} replace />;

  const isEmpty = draft != null && isDraftEmpty(draft);
  const pendingWrites = draft && state.kind === 'ready' ? toWrites(draft, state.baseline) : [];
  const changes = draft && state.kind === 'ready' ? diffCatalogue(draft, state.baseline) : [];
  const violations = validateChanges(changes);

  // An item already written server-side (503 unaudited) needs a reload, not a local undo, so it
  // is excluded from the discardable set.
  const unauditedIds = new Set((report ?? []).filter((r) => r.outcome.kind === 'unaudited').map((r) => r.id));
  const discardableIds = new Set(pendingWrites.map((w) => w.id).filter((id) => !unauditedIds.has(id)));

  const handleDiscard = (id: string) => {
    if (state.kind !== 'ready' || unauditedIds.has(id)) return;
    const baseline = state.baseline;
    const base = baseline.find((i) => i.id === id) ?? null;
    setDrawer(null); // a stale drawer Save could otherwise re-apply the discarded item
    setDraft((d) => (d ? revertChange(d, baseline, id) : d));
    logEvent(CHANGE_DISCARDED, 'Staff discarded a pending catalogue change',
      { product_id: product.id, item_id: id, kind: kindOfId(id), scope: base ? 'revert' : 'drop' }, 'INFO');
  };

  const handleDiscardAll = () => {
    if (state.kind !== 'ready' || pendingWrites.length === 0) return;
    setDrawer(null);
    setDraft(toDraft(state.baseline, product.id));
    logEvent(CHANGE_DISCARDED, 'Staff discarded all pending catalogue changes',
      { product_id: product.id, scope: 'all' }, 'INFO');
    setConfirmDiscardAll(false);
  };

  // Switching product unmounts the editor and loses the draft; warn first when work is pending.
  const handleProductChange = (v: string | null) => {
    if (!v) return;
    if (pendingWrites.length > 0 && !globalThis.confirm('Discard unpublished changes and switch product?')) return;
    navigate(`/staff/catalogue/${v}`);
  };

  // A disabled Publish button cannot open the dialog that would list violations, so the
  // violations render in an always-visible panel and this fires on the attempted publish.
  const handleAttemptPublish = () => {
    if (violations.length > 0) {
      logEvent(PUBLISH_BLOCKED, 'Staff catalogue publish blocked by validation',
        { product_id: product.id, fault_counts: faultCounts(violations) }, 'WARN');
      return;
    }
    setPublishOpen(true);
  };

  const handleToggleFeature = (tierSlug: string, feature: string) => {
    setDraft((d) => d && ({
      ...d,
      tiers: d.tiers.map((t) => (t.slug === tierSlug ? { ...t, features: toggleInList(t.features, feature) } : t)),
    }));
  };

  const handleAddTier = (slug: string) => {
    setDraft((d) => d && ({ ...d, tiers: [...d.tiers, { slug, features: [] }] }));
  };

  const handleSetTierStatus = (slug: string, status: ItemStatus) => {
    setDraft((d) => d && ({ ...d, tiers: d.tiers.map((t) => (t.slug === slug ? { ...t, status } : t)) }));
  };

  const handleSetModuleStatus = (slug: string, status: ItemStatus) => {
    setDraft((d) => d && ({ ...d, modules: d.modules.map((m) => (m.slug === slug ? { ...m, status } : m)) }));
  };

  const handleRetireOffer = (slug: string) => {
    setDraft((d) => d && ({ ...d, offers: d.offers.map((o) => (o.slug === slug ? { ...o, availability: 'retired' } : o)) }));
  };

  const handleSaveModule = (m: ModuleDraft) => {
    // Preserve an existing module's retire status — the drawer does not carry it.
    setDraft((d) => d && ({
      ...d,
      modules: d.modules.some((x) => x.slug === m.slug) ? d.modules.map((x) => (x.slug === m.slug ? { ...m, status: x.status } : x)) : [...d.modules, m],
    }));
    setDrawer(null);
  };

  const handleSaveOffer = (o: OfferDraft) => {
    setDraft((d) => d && ({
      ...d,
      offers: d.offers.some((x) => x.slug === o.slug) ? d.offers.map((x) => (x.slug === o.slug ? o : x)) : [...d.offers, o],
    }));
    setDrawer(null);
  };

  const handlePublish = async () => {
    if (!draft || state.kind !== 'ready') return;
    const baseline = state.baseline;
    setPublishing(true);
    setReport(null);
    try {
      const token = await auth.getAccessToken();
      if (!token) return;
      const writes = toWrites(draft, baseline);
      logEvent(PUBLISH_STARTED, 'Staff catalogue publish started', { product_id: product.id, write_count: writes.length });
      const rep = await publishPackaging(writes, makePut(token));
      setReport(rep);
      setState((s) => {
        if (s.kind !== 'ready') return s;
        let nextBaseline = s.baseline;
        for (const row of rep) {
          const write = writes.find((w) => w.id === row.id);
          if (write) nextBaseline = foldOutcome(nextBaseline, write, row.outcome);
        }
        return { kind: 'ready', baseline: nextBaseline };
      });
      const succeeded = rep.filter((row) => row.outcome.kind === 'published').length;
      logEvent(PUBLISH_COMPLETED, 'Staff catalogue publish completed',
        { product_id: product.id, write_count: writes.length, succeeded, failed: writes.length - succeeded });
      for (const row of rep) {
        const { outcome } = row;
        if (outcome.kind === 'published') {
          const write = writes.find((w) => w.id === row.id);
          if (write) logRetireTransition(write, baseline, product.id);
          continue;
        }
        if (outcome.kind === 'unaudited') {
          logEvent(WRITE_UNAUDITED, 'Catalogue write not recorded to the audit log',
            { product_id: product.id, item_id: row.id }, 'ERROR');
          continue;
        }
        const reason = outcome.kind === 'rejected' ? outcome.reason : undefined;
        const status = 'status' in outcome ? outcome.status : 0; // unreachable never got a response
        logEvent(WRITE_REJECTED, 'Catalogue write rejected',
          { product_id: product.id, item_id: row.id, status, reason }, 'WARN');
      }
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Stack flex={1} style={{ overflowY: 'auto' }} px={32} py={24} gap="lg">
      <Group justify="space-between" align="flex-end">
        <Group gap={12}>
          <img src={product.logo} alt="" width={28} height={28} style={{ objectFit: 'contain' }} />
          <Stack gap={2}>
            <Text fz="xl" fw={700} c="var(--text-default)">Catalogue</Text>
            <Text fz="sm" c="var(--text-subtle)">
              What customers can buy or try.{writable ? '' : ' Read-only.'}
            </Text>
          </Stack>
        </Group>
        <Group gap={12} align="flex-end">
          <Select
            aria-label="Product"
            data={catalogueProducts.map((p: CatalogueProduct) => ({ value: p.id, label: p.name }))}
            value={product.id}
            allowDeselect={false}
            onChange={handleProductChange}
            w={220}
          />
          {writable && pendingWrites.length > 0 && (
            <Button variant="default" onClick={() => setConfirmDiscardAll(true)}>Discard all</Button>
          )}
          {writable && (
            violations.length > 0 ? (
              <Button color="var(--status-danger)" variant="light" onClick={handleAttemptPublish}>
                {`Resolve ${violations.length} issue${violations.length === 1 ? '' : 's'}`}
              </Button>
            ) : (
              <Button onClick={() => setPublishOpen(true)} disabled={pendingWrites.length === 0 || publishing}>
                Publish
              </Button>
            )
          )}
        </Group>
      </Group>

      {state.kind === 'loading' && <Center py={40}><Loader /></Center>}
      {state.kind === 'error' && <CatalogueError status={state.status} onRetry={() => setReloadKey((k) => k + 1)} />}
      {state.kind === 'ready' && draft && isEmpty && !writable && <EmptyCatalogue />}
      {writable && violations.length > 0 && (
        <Alert role="alert" color="var(--status-danger)" variant="light"
          style={{ borderColor: 'var(--status-danger-background)', '--alert-bg': 'var(--status-danger-background)' }}>
          <Text fz="sm" fw={700} c="var(--status-danger)">Fix before publishing</Text>
          <List size="sm">
            {violations.map((v) => (
              <List.Item key={`${v.id}:${v.fault}`}>{`${slugOf(v.id)}: ${FAULT_COPY[v.fault]}`}</List.Item>
            ))}
          </List>
        </Alert>
      )}
      {state.kind === 'ready' && draft && (!isEmpty || writable) && (
        <CatalogueEditor
          draft={draft}
          writable={writable}
          onToggleFeature={handleToggleFeature}
          onAddTier={handleAddTier}
          onSetTierStatus={handleSetTierStatus}
          onSetModuleStatus={handleSetModuleStatus}
          onRetireOffer={handleRetireOffer}
          discardableIds={discardableIds}
          onDiscard={handleDiscard}
          onAddModule={(slug) => setDrawer({ kind: 'module', item: { slug, features: [], requiresTier: null } })}
          onEditModule={(m) => setDrawer({ kind: 'module', item: m })}
          onAddOffer={(slug) => setDrawer({ kind: 'offer', item: { slug, confers: [], durationDays: null, availability: 'self-serve', oncePerOrg: false } })}
          onEditOffer={(o) => setDrawer({ kind: 'offer', item: o })}
        />
      )}

      {writable && state.kind === 'ready' && draft && (
        <PublishDialog
          opened={publishOpen}
          changes={summarise(draft, state.baseline)}
          report={report}
          busy={publishing}
          warnings={retireWarnings(draft, state.baseline)}
          discardableItems={changes.filter((c) => discardableIds.has(c.id)).map((c) => c.id)}
          onDiscard={handleDiscard}
          onPublish={handlePublish}
          onClose={() => { setPublishOpen(false); setReport(null); }}
        />
      )}

      <Modal opened={confirmDiscardAll} onClose={() => setConfirmDiscardAll(false)} title="Discard all pending changes?" centered>
        <Stack gap="md">
          <Text fz="sm" c="var(--text-subtle)">This clears every unpublished change on this product and cannot be undone.</Text>
          <Group justify="flex-end" gap={10}>
            <Button variant="default" onClick={() => setConfirmDiscardAll(false)}>Keep editing</Button>
            <Button color="var(--status-danger)" onClick={handleDiscardAll}>
              {`Discard ${pendingWrites.length} change${pendingWrites.length === 1 ? '' : 's'}`}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {writable && draft && drawer?.kind === 'module' && (
        <ModuleDrawer
          opened
          item={drawer.item}
          tierSlugs={draft.tiers.map((t) => t.slug)}
          onSave={handleSaveModule}
          onClose={() => setDrawer(null)}
        />
      )}
      {writable && draft && drawer?.kind === 'offer' && (
        <OfferDrawer
          opened
          item={drawer.item}
          tierSlugs={draft.tiers.map((t) => t.slug)}
          moduleSlugs={draft.modules.map((m) => m.slug)}
          onSave={handleSaveOffer}
          onClose={() => setDrawer(null)}
        />
      )}
    </Stack>
  );
}

function CatalogueError({ status, onRetry }: Readonly<{ status: number; onRetry: () => void }>) {
  const message =
    status === 403 ? "You don't have staff access to the catalogue." :
    status === 401 ? 'Your session has expired — sign in again.' :
    "Couldn't reach the catalogue service. Please try again.";
  return (
    <Alert role="alert" color="var(--status-danger)" variant="light" p="xl"
      style={{ borderColor: 'var(--status-danger-background)', '--alert-bg': 'var(--status-danger-background)' }}>
      <Group justify="space-between" align="center">
        <Text c="var(--status-danger)" size="sm">{message}</Text>
        {status !== 403 && status !== 401 && <Button size="xs" variant="default" onClick={onRetry}>Try again</Button>}
      </Group>
    </Alert>
  );
}

function EmptyCatalogue() {
  return (
    <Center py={48}>
      <Stack align="center" gap={6}>
        <Text fz="sm" fw={600} c="var(--text-default)">No catalogue items yet</Text>
        <Text fz="xs" c="var(--text-subtle)">This product has no tiers, modules, or trial offers defined.</Text>
      </Stack>
    </Center>
  );
}

interface CatalogueEditorProps {
  draft: Draft;
  writable: boolean;
  onToggleFeature: (tierSlug: string, feature: string) => void;
  onAddTier: (slug: string) => void;
  onSetTierStatus: (slug: string, status: ItemStatus) => void;
  onSetModuleStatus: (slug: string, status: ItemStatus) => void;
  onRetireOffer: (slug: string) => void;
  discardableIds: ReadonlySet<string>;
  onDiscard: (id: string) => void;
  onAddModule: (slug: string) => void;
  onEditModule: (m: ModuleDraft) => void;
  onAddOffer: (slug: string) => void;
  onEditOffer: (o: OfferDraft) => void;
}

function CatalogueEditor({
  draft, writable, onToggleFeature, onAddTier, onSetTierStatus, onSetModuleStatus, onRetireOffer,
  discardableIds, onDiscard, onAddModule, onEditModule, onAddOffer, onEditOffer,
}: Readonly<CatalogueEditorProps>) {
  const tierSlugs = draft.tiers.map((t) => t.slug);
  const rows = featureGrid(draft.tiers);

  return (
    <Stack gap="xl">
      {tierSlugs.length > 0 ? (
        <Box style={{ border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)' }}>
          <Table>
            <Table.Thead>
              <Table.Tr style={{ background: 'var(--bg-subtle)', color: 'var(--text-subtle)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                <Table.Th>Feature</Table.Th>
                {draft.tiers.map((tier) => {
                  const retired = tier.status === 'retired';
                  return (
                    <Table.Th key={tier.slug} style={{ opacity: retired ? 0.55 : 1 }}>
                      <Group gap={4} wrap="nowrap">
                        <span>{tier.slug}</span>
                        {retired && <Badge size="xs" color="var(--status-warning)" aria-label={`${tier.slug} retired`}>Retired</Badge>}
                        {writable && (
                          <Button size="compact-xs" variant="subtle"
                            aria-label={`${retired ? 'Restore' : 'Retire'} ${tier.slug}`}
                            onClick={() => onSetTierStatus(tier.slug, retired ? 'available' : 'retired')}>
                            {retired ? 'Restore' : 'Retire'}
                          </Button>
                        )}
                        {writable && discardableIds.has(`tier:${tier.slug}`) && (
                          <Button size="compact-xs" variant="subtle" color="var(--status-danger)"
                            aria-label={`Remove ${tier.slug}`} onClick={() => onDiscard(`tier:${tier.slug}`)}>
                            Remove
                          </Button>
                        )}
                      </Group>
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.name}>
                  <Table.Td><Text fz="sm">{row.name}</Text></Table.Td>
                  {tierSlugs.map((t) => (
                    <Table.Td key={t}>
                      {writable ? (
                        <Checkbox
                          aria-label={`${row.name} on ${t}`}
                          checked={row.grantedBy[t]}
                          onChange={() => onToggleFeature(t, row.name)}
                        />
                      ) : grantIcon(row.grantedBy[t])}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      ) : (
        <Text fz="sm" c="var(--text-subtle)">No tiers defined for this product yet.</Text>
      )}

      {writable && (
        <Group gap={16}>
          <AddSlugControl label="tier" existingSlugs={tierSlugs} onAdd={onAddTier} />
          <AddFeatureControl tierSlugs={tierSlugs} onAdd={onToggleFeature} />
        </Group>
      )}

      <Shelf title="Modules · à la carte" empty={draft.modules.length === 0}
        action={writable ? <AddSlugControl label="module" existingSlugs={draft.modules.map((m) => m.slug)} onAdd={onAddModule} /> : undefined}>
        {draft.modules.map((m) => {
          const requirement = m.requiresTier ? `requires ${m.requiresTier}` : 'available on every tier';
          const featureCount = `${m.features.length} feature${m.features.length === 1 ? '' : 's'}`;
          const retired = m.status === 'retired';
          return (
            <ShelfCard key={m.slug} title={m.slug} subtitle={`${requirement} · ${featureCount}`} retired={retired}
              onEdit={writable ? () => onEditModule(m) : undefined}
              statusAction={writable ? { label: retired ? 'Restore' : 'Retire', onClick: () => onSetModuleStatus(m.slug, retired ? 'available' : 'retired') } : undefined}
              onRemove={writable && discardableIds.has(`module:${m.slug}`) ? () => onDiscard(`module:${m.slug}`) : undefined} />
          );
        })}
      </Shelf>

      <Shelf title="Trial offers" empty={draft.offers.length === 0}
        action={writable ? <AddSlugControl label="offer" existingSlugs={draft.offers.map((o) => o.slug)} onAdd={onAddOffer} /> : undefined}>
        {draft.offers.map((o) => {
          const confers = o.confers.map((c) => `${c.kind}:${c.slug}`).join(', ') || '—';
          const oncePerOrg = o.oncePerOrg ? ' · once per org' : '';
          const subtitle = `${o.durationDays ?? '—'} days · confers ${confers} · ${o.availability ?? '—'}${oncePerOrg}`;
          const retired = o.availability === 'retired';
          // Retire is a direct availability change; restore reopens the drawer so the channel
          // (self-serve / sales-only) is chosen explicitly rather than silently defaulted.
          const restoreAction = { label: 'Restore', onClick: () => onEditOffer(o) };
          const retireAction = { label: 'Retire', onClick: () => onRetireOffer(o.slug) };
          const toggleAction = retired ? restoreAction : retireAction;
          const statusAction = writable ? toggleAction : undefined;
          return (
            <ShelfCard key={o.slug} title={o.slug} subtitle={subtitle} retired={retired}
              onEdit={writable ? () => onEditOffer(o) : undefined}
              statusAction={statusAction}
              onRemove={writable && discardableIds.has(`trial-offer:${o.slug}`) ? () => onDiscard(`trial-offer:${o.slug}`) : undefined} />
          );
        })}
      </Shelf>
    </Stack>
  );
}

// Tier/module/offer slugs are fixed at creation (tiers have no drawer at all; ModuleDrawer and
// OfferDrawer both disable their Slug field), so "add" collects the slug here first — a module
// or offer created this way opens its drawer pre-seeded but not yet part of the draft; onSave
// (handleSaveModule/handleSaveOffer) appends it since no existing item matches that slug yet.
// Inline copy for a slug fault. Only the slug faults are reachable here; the rest of the
// Fault union belongs to the publish-time gate (validateChanges), so they map to ''.
const SLUG_FAULT_COPY: Record<Fault, string> = {
  slug_missing: 'Enter a slug',
  slug_malformed: 'Lowercase letters, numbers, hyphen or underscore only',
  slug_too_long: 'Too long (max 100 characters)',
  already_exists: 'That slug is already used — restore the retired item instead if it is retired',
  feature_name_blank: '', confers_nothing: '', duration_out_of_range: '',
};

function AddSlugControl({ label, existingSlugs, onAdd }: Readonly<{ label: string; existingSlugs: readonly string[]; onAdd: (slug: string) => void }>) {
  const [slug, setSlug] = useState('');
  const trimmed = slug.trim();
  const fault: Fault | null = trimmed.length === 0 ? 'slug_missing' : slugFault(trimmed, existingSlugs);
  return (
    <Group gap={8} align="flex-start">
      <TextInput size="xs" placeholder={`new-${label}-slug`} aria-label={`New ${label} slug`}
        value={slug} onChange={(e) => setSlug(e.currentTarget.value)}
        error={slug.length > 0 && fault ? SLUG_FAULT_COPY[fault] : undefined} />
      <Button size="xs" variant="default" disabled={fault !== null}
        onClick={() => { onAdd(trimmed); setSlug(''); }}>
        {`Add ${label}`}
      </Button>
    </Group>
  );
}

// A "new feature row" only exists once at least one tier grants it (featureGrid derives rows
// from tiers' features), so adding one is the same immutable toggle as any cell — just against
// a feature name that isn't in any tier's features yet, on a tier the staff member picks.
function AddFeatureControl({ tierSlugs, onAdd }: Readonly<{ tierSlugs: string[]; onAdd: (tierSlug: string, feature: string) => void }>) {
  const [feature, setFeature] = useState('');
  const [tierSlug, setTierSlug] = useState(tierSlugs[0] ?? '');
  if (tierSlugs.length === 0) return null;
  const targetTier = tierSlugs.includes(tierSlug) ? tierSlug : tierSlugs[0];
  return (
    <Group gap={8}>
      <TextInput size="xs" placeholder="new-feature-name" aria-label="New feature name"
        value={feature} onChange={(e) => setFeature(e.currentTarget.value)} />
      <Select size="xs" w={140} aria-label="Grant to tier" data={tierSlugs} allowDeselect={false}
        value={targetTier} onChange={(v) => v && setTierSlug(v)} />
      <Button size="xs" variant="default" disabled={!feature.trim()}
        onClick={() => { onAdd(targetTier, feature.trim()); setFeature(''); }}>
        Add feature
      </Button>
    </Group>
  );
}

function Shelf({ title, empty, action, children }: Readonly<{ title: string; empty: boolean; action?: ReactNode; children: ReactNode }>) {
  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text fz="xs" fw={700} tt="uppercase" c="var(--text-subtle)">{title}</Text>
        {action}
      </Group>
      {empty ? <Text fz="sm" c="var(--text-subtle)">None.</Text> : children}
    </Stack>
  );
}

interface ShelfCardProps {
  title: string;
  subtitle: string;
  retired?: boolean;
  onEdit?: () => void;
  statusAction?: { label: string; onClick: () => void };
  onRemove?: () => void;
}

function ShelfCard({ title, subtitle, retired = false, onEdit, statusAction, onRemove }: Readonly<ShelfCardProps>) {
  return (
    <Box p={14} style={{ opacity: retired ? 0.55 : 1, border: '1px solid var(--border-default)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Group gap={6}>
            <Text fz="sm" fw={600}>{title}</Text>
            {retired && <Badge size="xs" color="var(--status-warning)" aria-label={`${title} retired`}>Retired</Badge>}
          </Group>
          <Text fz="xs" c="var(--text-subtle)">{subtitle}</Text>
        </Stack>
        <Group gap={6}>
          {onEdit && <Button size="xs" variant="subtle" onClick={onEdit}>Edit</Button>}
          {statusAction && (
            <Button size="xs" variant="subtle" aria-label={`${statusAction.label} ${title}`} onClick={statusAction.onClick}>
              {statusAction.label}
            </Button>
          )}
          {onRemove && (
            <Button size="xs" variant="subtle" color="var(--status-danger)" aria-label={`Remove ${title}`} onClick={onRemove}>
              Remove
            </Button>
          )}
        </Group>
      </Group>
    </Box>
  );
}
